import { Clock } from 'lucide-react';
import { useState, useEffect, useRef, type KeyboardEvent } from 'react';
import { namesApplicableSetting } from '../utils/settingsChangeText';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button, Callout, ConfirmDialog } from '@/shared/ui';
import { formatDate, formatTimestamp } from '@/shared/utils/format';
import type {
  AgentMessage,
  AgentTask,
  AgentTaskWithdrawalKind,
  ContentPublication,
  Recommendation,
} from '../types';
import { useTaskThread } from '../hooks/useTaskThread';
import { useTaskVersions } from '../hooks/useTaskVersions';
import { taskActions } from '../utils/taskActions';
import { BUCKET_TAB_LABEL } from './labels';
import { floorLabel } from '../utils/agentLabels';
import { sanitizeReportText } from '../utils/reportText';
import { splitScoutReport } from '../utils/joePicks';
import { adsTake } from '../utils/adsReport';
import { BETTY_PROPOSAL_SKILLS } from '../utils/reviewsReport';
import { SAM_PANEL_SKILLS } from '../utils/samTasks';
import { normalizeRecommendation } from '../utils/proposals';
import { retryNoticeCopy } from '../utils/retryNotice';
import { AD_CHANGE_COULD_NOT_DRAFT_LABEL, WITHDRAWN_LABEL } from '../utils/unifiedTasks';
import { DraftSections } from './DraftSections';
import { AuditFindings } from './AuditFindings';
import { JOE_SKILLS, JOE_SKILLS_WITH_OWN_ACTIONS, JoeTaskPanel } from './JoeTaskPanel';
import { JoeWeekPicks } from './JoeWeekPicks';
import { JennyAdsReport } from './JennyAdsReport';
import { writtenSection } from '../utils/adsSnapshot';
import { BettyReviewsReport } from './BettyReviewsReport';
import { BettyProposalPanel } from './BettyProposalPanel';
import { SamProposalPanel } from './SamProposalPanel';
import { AdsChangeStatus } from './AdsChangeStatus';
import { adsChangeMadeByHand } from '../utils/adsChangeStatus';
import { DecisionSlot } from './DecisionSlot';
import { replyQuoteText } from '../utils/conversationPilot';
import styles from '../pages/TasksPage.module.css';
import tableStyles from '@/shared/ui/Table/Table.module.css';

// A report's markdown tables (e.g. the profile snapshot) look exactly like the shared Table that
// the Findings tables use.
const REPORT_MARKDOWN: Components = {
  table: ({ children }) => (
    <div className={tableStyles.tableWrapper}>
      <table className={tableStyles.table}>{children}</table>
    </div>
  ),
  tr: ({ children }) => <tr className={tableStyles.tr}>{children}</tr>,
  th: ({ children }) => <th className={tableStyles.th}>{children}</th>,
  td: ({ children }) => <td className={tableStyles.td}>{children}</td>,
};

// The middle + right columns for the SELECTED task: the versioned output pane and the thread pane.
// They share which version is being VIEWED, so clicking a ✦ vN tag in the thread jumps the output
// pane to that version. Mounted with a `key={task.id}` by the page, so switching tasks remounts this
// and the viewed-version / composer / active-tab / reject-dialog state all reset on their own — no
// cross-task leakage to clean up.

// How cancelled work was closed. A rejection is the default: it is the only one that had a draft.
const CANCELLED_LABEL = {
  rejected: 'Rejected',
  skipped: 'Skipped',
  cancelled: 'Skipped, cancelled before work started',
  no_change: 'Closed, no change',
  ...WITHDRAWN_LABEL,
} as const;

/** Open work the agency can withdraw (agent-suite's WITHDRAWABLE_STATES). */
const WITHDRAWABLE_STATES: ReadonlySet<AgentTask['state']> = new Set([
  'ready',
  'awaiting_approval',
  'needs_review',
  'blocked',
]);

export function TaskWorkspace({
  task,
  locationId,
  onApprove,
  onReject,
  onReview,
  onRetryPublish,
  onSkip,
  onCancel,
  onWithdraw,
  busy = false,
  reviewBusy = false,
  retryPublishBusy = false,
  agentName = 'Your agent',
  embedded = false,
  viewingVersion: controlledVersion,
  onViewingVersionChange,
  onAskForChanges,
  onAsk,
}: {
  task: AgentTask;
  locationId: string;
  onApprove?: (taskId: string) => void;
  onReject?: (taskId: string, reason?: string) => void;
  /** Acknowledge a read-only audit or failed work ("Mark reviewed") — clears it to History. */
  onReview?: (taskId: string) => void;
  /** Send an approved Business Profile post to Google again after a failed publish. */
  onRetryPublish?: (taskId: string) => void;
  /** Skip an open schedule draft (no replacement is written) instead of rejecting it. */
  onSkip?: (taskId: string, reason?: string) => void;
  /** Cancel queued work before the agent starts it (nothing replaces it). */
  onCancel?: (taskId: string, reason?: string) => void;
  /** Agency only: withdraw open work as out of date, or as done outside PracticeNova. Absent for
   *  practice users, which hides the control. */
  onWithdraw?: (taskId: string, kind: AgentTaskWithdrawalKind, reason?: string) => void;
  busy?: boolean;
  reviewBusy?: boolean;
  retryPublishBusy?: boolean;
  /** The persona name of the agent that owns this task (Sam, Jenny, …), for the thread and copy. */
  agentName?: string;
  /** Rendered inside the conversation view (ConversationWorkspace): no Draft/Thread tabs, since the
   *  thread is the conversation around it, and the card grows with its content instead of scrolling. */
  embedded?: boolean;
  /** Controlled version selection, so the conversation's ✦ vN tags can pick the version shown. */
  viewingVersion?: number | null;
  onViewingVersionChange?: (n: number | null) => void;
  /** Where "Ask for changes" goes when there is no Thread tab (the conversation's message box). */
  onAskForChanges?: () => void;
  /** Puts a ready-made question in the conversation's message box ("Ask Jenny about this"). */
  onAsk?: (question: string) => void;
}) {
  // Which of the two tabs is showing — the primary content (draft or report) or the thread.
  const [tab, setActiveTab] = useState<'primary' | 'thread'>('primary');
  const activeTab = embedded ? 'primary' : tab;
  // null = "follow the current version"; a number = pinned to a specific version the user clicked.
  const [localVersion, setLocalVersion] = useState<number | null>(null);
  const viewingVersion = controlledVersion !== undefined ? controlledVersion : localVersion;
  const setViewingVersion = onViewingVersionChange ?? setLocalVersion;
  const openThread = () => (onAskForChanges ? onAskForChanges() : setActiveTab('thread'));
  // Reject, Skip, Cancel and Withdraw share one dialog: each closes the work out and takes an optional reason.
  const [dialog, setDialog] = useState<'reject' | 'skip' | 'cancel' | 'withdraw' | null>(null);
  const [withdrawKind, setWithdrawKind] = useState<AgentTaskWithdrawalKind>('out_of_date');
  const [dialogReason, setDialogReason] = useState('');
  const { versions, current, restore, restoring } = useTaskVersions(task.id, locationId, true);

  const p = task.payload;
  const accessAsks = p?.accessAsks ?? [];
  const hasVersions = versions.length > 0;
  const effectiveVersion = viewingVersion ?? current;
  const selectedVersion = versions.find((v) => v.n === effectiveVersion);
  const isViewingOld = hasVersions && effectiveVersion !== current;
  // Prefer the selected version's proposals; fall back to the task payload for a pre-versioning task
  // that hasn't been chatted yet (it has payload.proposals but no version rows).
  const proposals: Recommendation[] =
    hasVersions && selectedVersion ? selectedVersion.proposals : (p?.proposals ?? []);
  const hasDraft = proposals.length > 0;
  // Engine drafts carry their fields laid out by the skill's output template. Read them from the
  // version being viewed, so switching v1/v2 shows that version, not always the latest.
  const sections = hasVersions && selectedVersion ? selectedVersion.sections : p?.sections;
  const hasSections = !!sections && sections.length > 0;
  // Task D4 fix round 0/2: a task with no structured proposals (every audit/report -- engine drafts
  // always set proposals: []) previously always showed payload.summary here regardless of which
  // version tab was selected, so previewing an OLD version before restoring it showed the CURRENT
  // version's text. Fix round 2: the DEFAULT view (no old version selected) must render exactly
  // what every task type already rendered before this task -- payload.summary, which the inventory
  // pins for legacy report/audit tasks. Only diverge from it while `isViewingOld`: show that
  // specific version's own body. `p?.summary` for the current version already agrees with the
  // engine's own currentDraftFor-resolved body (Fix round 0's backend change), so this changes
  // nothing about the engine behavior already verified live -- only the legacy default-view
  // regression is removed.
  const draftBody = isViewingOld && selectedVersion ? selectedVersion.body : p?.summary;
  const isAudit = task.bucket === 'audit';
  // An audit's findings are laid out from data, and belong to its latest run, so an older version
  // shows only its own written report.
  const findings = isAudit && !isViewingOld ? (p?.findings ?? []) : [];
  const showsSummary = !hasSections && !!draftBody && !hasDraft;
  // Queued or in-progress work with nothing to show yet. Its empty state says where the work is,
  // never "complete" or "no proposal", which only describe finished work.
  const awaitingAgent =
    (task.state === 'ready' || task.state === 'in_progress') &&
    !hasDraft &&
    !hasSections &&
    !showsSummary &&
    findings.length === 0 &&
    accessAsks.length === 0;
  // Why the last attempt failed and when the next one is, in the owner's words (retryNotice.ts).
  const retryNotice = p?.retry
    ? retryNoticeCopy({ agentName, bucket: task.bucket, retry: p.retry, now: Date.now() })
    : undefined;
  const actions = taskActions(task);
  // Joe's action drafts (community) carry their own action bar: approving them first writes the
  // planned touch or the confirmed counts to the Community module, then approves the task.
  const isJoeTask = task.agentType === 'community' && JOE_SKILLS.has(task.skillKey);
  const joeOwnsActions = isJoeTask && JOE_SKILLS_WITH_OWN_ACTIONS.has(task.skillKey);
  // Joe's weekly picks: the picks themselves are shown live from the Community module, so what the
  // owner does there (or here) shows in both places. An older version still shows its own text.
  const isJoeScoutLive =
    isJoeTask && task.skillKey === 'community_scout' && !isViewingOld && !!locationId;
  const scoutReport = splitScoutReport(isJoeScoutLive ? sanitizeReportText(draftBody ?? '') : '');
  // Betty's weekly reviews report, drawn from the reads it cites; an older version shows as text.
  const reviewsReport =
    task.skillKey === 'review_funnel_report' && !isViewingOld && !!draftBody && p?.reviewsReport
      ? p.reviewsReport
      : null;
  // Jenny's weekly ads report, drawn from the figures of the performance read it cites. Her written
  // report stays one click away; an older version, or a report with no figures, shows as text.
  const adsReport =
    task.skillKey === 'ad_reporting' &&
    !isViewingOld &&
    !!draftBody &&
    p?.adsReport &&
    adsTake(p.adsReport)
      ? p.adsReport
      : null;
  const canAct = actions.canApproveOrReject && !!onApprove && !!onReject && !joeOwnsActions;
  // A draft held by its checks cannot be approved, but the owner can still reject it or ask for a
  // corrected version instead of being left with no way forward.
  const heldForReview = task.bucket === 'action' && task.state === 'needs_review' && !!onReject;
  // Why it is held, shown as a banner above the content rather than a line under it.
  const heldReason =
    task.state === 'needs_review' && task.blockedReason
      ? sanitizeReportText(task.blockedReason)
      : '';
  // An approved Business Profile post or listing edit goes to Google straight away, so approving is
  // the go-live decision and the owner should see that before clicking. A listing edit the agent
  // marked "Manual change" is the exception: the practice makes it, and approving only closes it.
  const isListingEdit = task.skillKey === 'gbp_listing_edit';
  const isManualChange =
    isListingEdit &&
    p?.sections?.find((section) => section.key === 'edit')?.value === 'Manual change';
  // Betty's proposals: approving applies the setting, or starts the two-week test, on the texting
  // program. A setup step or routine change with no "field: old -> new" line is done by hand.
  const sectionValue = (key: string) => p?.sections?.find((section) => section.key === key)?.value;
  const bettyApplies =
    task.skillKey === 'send_timing_tune' ||
    task.skillKey === 'invite_copy_tune' ||
    ((task.skillKey === 'review_program_setup' || task.skillKey === 'front_desk_routine_coach') &&
      namesApplicableSetting(String(sectionValue('settingsChange') ?? '')));
  const bettyTest = /two-week test/i.test(String(sectionValue('rollout') ?? ''));
  // A go-live step or routine change with no setting to switch is done by the practice: approving
  // only marks it done (the go-live step says so on its button).
  const doneByHand = task.skillKey === 'review_program_setup' && !bettyApplies;
  const routineByHand = task.skillKey === 'front_desk_routine_coach' && !bettyApplies;
  // Sam's search content and website fixes are made on the site by the practice, not by Sam.
  // A website edit is sent to the site as a pull request for a person to merge, unless the agent
  // marked it "Manual change" (a site we do not host): then the practice makes it.
  const isWebsiteEdit = task.skillKey === 'site_page_edit';
  const isWebsiteManual = isWebsiteEdit && sectionValue('change') === 'Manual change';
  const appliedByHand =
    task.skillKey === 'gsc_content_gap' ||
    task.skillKey === 'site_technical_fix' ||
    isWebsiteManual;
  const publishesOnApproval =
    task.skillKey === 'gbp_post' ||
    (isListingEdit && !isManualChange) ||
    (isWebsiteEdit && !isWebsiteManual) ||
    bettyApplies;
  // Jenny's ad changes are applied in Google Ads on approval (checked against the live setting first,
  // read back after, and logged as made through PracticeNova); the owner can undo one.
  const isAdsChange = task.skillKey === 'ad_campaign_change';
  // A draft written before changes were applied told the owner to make it themselves: it is never sent.
  const adsByHand = isAdsChange && adsChangeMadeByHand(String(sectionValue('change') ?? ''));
  const publishNoun = bettyApplies
    ? 'setting'
    : isWebsiteEdit
      ? 'page'
      : isListingEdit
        ? 'change'
        : 'post';
  // A draft that fills a quota slot (e.g. this week's post): rejecting it brings a replacement, so
  // skipping is offered as the way to go without one.
  const slot = p?.slot;
  const canSkip = !!slot && !!onSkip;
  // What the skill calls the item (its manifest's skip label); a skill without one reads "this one".
  const skipNoun = slot?.skipLabel ?? 'one';
  // Queued work the agent has not started can be cancelled.
  const canCancel = task.state === 'ready' && !task.replying && !!onCancel;
  // Agency only: open work that no longer applies (the data moved on, or the change was already made
  // in the ad account). A quota slot is skipped instead, so it is not offered there.
  const canWithdraw =
    !!onWithdraw && WITHDRAWABLE_STATES.has(task.state) && !task.replying && !slot;
  // Cancelled before anything was written: there is no draft or report to show.
  const cancelledUnstarted =
    task.state === 'cancelled' &&
    p?.cancelKind !== 'no_change' &&
    !hasDraft &&
    !hasSections &&
    !showsSummary &&
    findings.length === 0 &&
    accessAsks.length === 0;
  const closeDialog = () => {
    setDialog(null);
    setDialogReason('');
    setWithdrawKind('out_of_date');
  };
  const publication = p?.publication;

  return (
    <div
      className={embedded ? `${styles.detailPane} ${styles.detailPaneEmbedded}` : styles.detailPane}
    >
      {/* Title, state, skill and timestamp are deliberately NOT repeated here — they're already on
       * this task's card in the list to its left. Only the version picker (not shown there) earns a
       * place in this header. */}
      {(!embedded || hasVersions) && (
        <div className={styles.tabs}>
          {embedded ? (
            <span className={styles.tiny}>{BUCKET_TAB_LABEL[task.bucket]}</span>
          ) : (
            <div className={styles.tabGroup}>
              <button
                type="button"
                className={
                  activeTab === 'primary' ? `${styles.tab} ${styles.tabActive}` : styles.tab
                }
                onClick={() => setActiveTab('primary')}
              >
                {BUCKET_TAB_LABEL[task.bucket]}
              </button>
              <button
                type="button"
                className={
                  activeTab === 'thread' ? `${styles.tab} ${styles.tabActive}` : styles.tab
                }
                onClick={() => setActiveTab('thread')}
              >
                Thread
              </button>
            </div>
          )}
          {hasVersions && (
            <div className={styles.versionBar}>
              {versions.map((v) => (
                <button
                  key={v.n}
                  type="button"
                  className={
                    v.n === effectiveVersion
                      ? `${styles.versionBtn} ${styles.versionBtnActive}`
                      : styles.versionBtn
                  }
                  onClick={() => {
                    setViewingVersion(v.n);
                    // A version is what the Report tab shows: picking one from the Thread tab used to
                    // change nothing on screen.
                    setActiveTab('primary');
                  }}
                  aria-pressed={v.n === effectiveVersion}
                >
                  v{v.n}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'thread' ? (
        <ThreadPane
          taskId={task.id}
          locationId={locationId}
          onVersionTagClick={(n) => {
            // The ✦ vN tag lives in the Thread tab; the version itself shows in the Report tab.
            setViewingVersion(n);
            setActiveTab('primary');
          }}
          agentName={agentName}
        />
      ) : (
        <div className={styles.scroll}>
          {floorLabel(p?.floorReason) && (
            <span
              className={styles.floorBadge}
              title="This step is always yours to approve, whatever your notification preferences."
            >
              Needs your decision: {floorLabel(p?.floorReason)}
            </span>
          )}
          {heldReason && (
            // A draft held back by its final check: say what is going on and what the owner can do.
            <div className={styles.heldBanner} role="status">
              <Clock size={18} aria-hidden="true" className={styles.heldIcon} />
              <span>
                <strong>{heldReason}</strong>{' '}
                {heldForReview
                  ? `You can read it now. Ask ${agentName} for changes to get a version you can approve, or reject it.`
                  : 'You can still read it.'}
              </span>
            </div>
          )}
          {/* The freeform run summary is only shown when there is no structured draft (audits,
           * access asks, no-proposal tasks). Above a proposal card it just restates the card, so
           * it is suppressed there — this also drops the long intro on legacy rows with no data change. */}
          {isJoeScoutLive ? (
            // Joe's report as his take when it is short; a long one (it lists every pick again) is
            // folded behind "Read Joe's full note", and the picks below are what the owner acts on.
            <JoeWeekPicks
              locationId={locationId}
              take={scoutReport.take}
              fullNote={
                scoutReport.fullNote ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={REPORT_MARKDOWN}>
                    {scoutReport.fullNote}
                  </ReactMarkdown>
                ) : null
              }
            />
          ) : adsReport ? (
            <JennyAdsReport
              report={adsReport}
              agentName={agentName}
              onAsk={onAsk}
              writtenTake={writtenSection(
                sanitizeReportText(draftBody ?? ''),
                `${agentName}'s take`,
              )}
              fullReport={
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={REPORT_MARKDOWN}>
                  {sanitizeReportText(draftBody ?? '')}
                </ReactMarkdown>
              }
            />
          ) : reviewsReport ? (
            <BettyReviewsReport
              report={reviewsReport}
              agentName={agentName}
              fullReport={
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={REPORT_MARKDOWN}>
                  {sanitizeReportText(draftBody ?? '')}
                </ReactMarkdown>
              }
            />
          ) : SAM_PANEL_SKILLS.has(task.skillKey) && hasSections ? (
            <SamProposalPanel
              skillKey={task.skillKey}
              sections={sections!}
              searchTerm={isViewingOld ? undefined : p?.searchTerm}
              agentName={agentName}
            />
          ) : BETTY_PROPOSAL_SKILLS.has(task.skillKey) && hasSections ? (
            <BettyProposalPanel
              skillKey={task.skillKey}
              sections={sections!}
              goLive={isViewingOld ? undefined : p?.goLive}
              agentName={agentName}
            />
          ) : isJoeTask && hasSections ? (
            <JoeTaskPanel
              task={task}
              sections={sections}
              locationId={locationId}
              version={hasVersions ? effectiveVersion : null}
              busy={busy || isViewingOld}
              onApprove={onApprove}
              onReject={onReject}
              onOpenThread={openThread}
            />
          ) : hasSections ? (
            <DraftSections sections={sections!} closed={task.state === 'cancelled'} />
          ) : (
            showsSummary && (
              <div className={styles.summary}>
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={REPORT_MARKDOWN}>
                  {sanitizeReportText(draftBody)}
                </ReactMarkdown>
              </div>
            )
          )}
          {findings.length > 0 && <AuditFindings findings={findings} />}
          {isJoeTask && isAudit && !hasSections && !isJoeScoutLive && (
            <JoeTaskPanel
              task={task}
              sections={sections}
              locationId={locationId}
              version={null}
              busy={busy}
              onOpenThread={openThread}
            />
          )}

          {isViewingOld && (
            <div className={styles.versionBanner}>
              <span>
                Viewing v{effectiveVersion} — not the current version (current is v{current}).
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={restoring}
                onClick={() => restore(effectiveVersion)}
              >
                Make v{effectiveVersion} current
              </Button>
            </div>
          )}

          {awaitingAgent ? (
            retryNotice ? (
              <Callout variant="warning" title={retryNotice.title}>
                {retryNotice.body}
              </Callout>
            ) : (
              <div className={styles.paneEmpty}>
                <div className={styles.paneEmptyBig}>
                  {task.state === 'in_progress'
                    ? `${agentName} is working on this`
                    : `${agentName} hasn't started this yet`}
                </div>
                {task.state === 'in_progress'
                  ? 'The result will appear here when it is ready.'
                  : `It is queued; ${agentName} will pick it up in turn.`}
              </div>
            )
          ) : cancelledUnstarted ? (
            <div className={styles.paneEmpty}>
              <div className={styles.paneEmptyBig}>Nothing was written</div>
              This was cancelled before {agentName} started it.
            </div>
          ) : hasDraft ? (
            <div className={styles.proposalList}>
              {proposals.map((raw, i) => {
                const pr = normalizeRecommendation(raw);
                return (
                  <div key={i} className={styles.proposal}>
                    <div className={styles.proposalChange}>
                      <ReactMarkdown>{pr.title}</ReactMarkdown>
                    </div>
                    <div className={styles.proposalMeta}>
                      <ReactMarkdown>{pr.description}</ReactMarkdown>
                    </div>
                    <div className={styles.proposalMeta}>
                      <span className={styles.proposalLabel}>Why</span>
                      <ReactMarkdown>{pr.why}</ReactMarkdown>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : accessAsks.length === 0 ? (
            isAudit ? (
              // The footer already says there is nothing to approve, so this only fills a report
              // that has nothing else to show.
              !showsSummary &&
              findings.length === 0 && (
                <div className={styles.paneEmpty}>
                  <div className={styles.paneEmptyBig}>
                    {p?.reportTitle ? 'Report ready' : 'Check complete'}
                  </div>
                  {agentName} finished this check and has nothing to add to it. If {agentName} spots
                  something worth doing, it opens that as its own task.
                </div>
              )
            ) : hasSections || draftBody ? null : (
              // An engine draft is its summary text (shown above), not a legacy proposal card, so
              // "no versioned proposal" would contradict the draft the owner is looking at.
              <div className={styles.paneEmpty}>
                <div className={styles.paneEmptyBig}>No versioned proposal</div>
                This task doesn&apos;t have an editable draft — see its summary above and the
                thread.
              </div>
            )
          ) : null}

          {accessAsks.map((a, i) => (
            <div key={i} className={styles.proposal}>
              <div className={styles.proposalChange}>{a.subject}</div>
              <div className={styles.proposalMeta}>
                <ReactMarkdown>{a.body}</ReactMarkdown>
              </div>
            </div>
          ))}

          {task.blockedReason && !heldReason && (
            <span className={styles.blocked}>{sanitizeReportText(task.blockedReason)}</span>
          )}
        </div>
      )}

      {activeTab === 'primary' && (
        <DecisionSlot>
          {canAct && (
            <div className={styles.actions}>
              <Button
                variant="primary"
                size="sm"
                disabled={busy || isViewingOld}
                title={isViewingOld ? 'Restore this version before approving' : undefined}
                onClick={() => onApprove?.(task.id)}
              >
                {doneByHand
                  ? 'Mark as done'
                  : task.skillKey === 'gbp_post'
                    ? 'Approve and publish'
                    : 'Approve'}
              </Button>
              {/* The conversation view's way to iterate before deciding (opens its change prompt). */}
              {embedded && onAskForChanges && (
                <Button variant="secondary" size="sm" disabled={busy} onClick={onAskForChanges}>
                  Make changes
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setDialog('reject')}
              >
                Reject
              </Button>
              {canSkip && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setDialog('skip')}
                >
                  {`Skip this ${skipNoun}`}
                </Button>
              )}
              {isAdsChange && (
                <span className={styles.tiny}>
                  {adsByHand
                    ? 'Approving records this change. It is not sent to Google or Meta.'
                    : 'Approving applies this change in Google Ads. PracticeNova first checks each setting is still what this shows, and changes nothing if it is not.'}
                </span>
              )}
              {appliedByHand && (
                <span className={styles.tiny}>
                  Records your approval. Nothing changes on your website until you or your web
                  person make the change.
                </span>
              )}
              {doneByHand && (
                <span className={styles.tiny}>
                  Changes no settings. {agentName} checks your texting setup again before the next
                  step.
                </span>
              )}
              {routineByHand && (
                <span className={styles.tiny}>
                  Changes no settings. It records the new routine, and {agentName} reports on it in
                  the weekly reviews report.
                </span>
              )}
              {publishesOnApproval && (
                <span className={styles.tiny}>
                  {bettyApplies
                    ? bettyTest
                      ? 'Approving starts a two-week test on half of new texts. The other half keep today’s.'
                      : 'Approving applies this to your text review settings.'
                    : isWebsiteEdit
                      ? 'Approving sends this change to your website for review. It goes live once it is merged.'
                      : isListingEdit
                        ? 'Approving applies this change to your Google Business Profile.'
                        : 'Approving publishes this post to your Google Business Profile.'}
                </span>
              )}
            </div>
          )}
          {!canAct && adsByHand && task.state === 'done' && (
            <div className={styles.noteBar}>
              Approved — recorded, not sent to Google or Meta. Make this change in the ad account.
            </div>
          )}
          {!canAct && isAdsChange && !adsByHand && task.state === 'done' && (
            <AdsChangeStatus
              publication={publication}
              taskId={task.id}
              locationId={locationId}
              busy={retryPublishBusy}
              onRetry={onRetryPublish ? () => onRetryPublish(task.id) : undefined}
            />
          )}
          {!canAct && publishesOnApproval && task.state === 'done' && (
            <PublicationStatus
              publication={publication}
              noun={publishNoun}
              busy={retryPublishBusy}
              onRetry={onRetryPublish ? () => onRetryPublish(task.id) : undefined}
            />
          )}
          {heldForReview && (
            <div className={styles.actions}>
              <Button variant="secondary" size="sm" onClick={openThread}>
                Ask for changes
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setDialog('reject')}
              >
                Reject
              </Button>
              {canSkip && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy}
                  onClick={() => setDialog('skip')}
                >
                  {`Skip this ${skipNoun}`}
                </Button>
              )}
              <span className={styles.tiny}>
                Approve is unavailable until the draft passes its checks.
              </span>
            </div>
          )}
          {!canAct && task.state === 'cancelled' && (
            <div className={styles.noteBar}>
              {p?.cancelKind === 'no_change' && p.couldNotDraft
                ? AD_CHANGE_COULD_NOT_DRAFT_LABEL
                : CANCELLED_LABEL[p?.cancelKind ?? 'rejected']}
              {task.cancelReason ? ` — ${task.cancelReason}` : ''}
            </div>
          )}
          {canCancel && (
            <div className={styles.actions}>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={() => setDialog('cancel')}
              >
                Cancel task
              </Button>
              {isAudit && (
                <span className={styles.tiny}>
                  Scheduled — results appear here once this cycle runs.
                </span>
              )}
            </div>
          )}
          {!canAct && actions.canMarkReviewed && (
            <div className={styles.actions}>
              <Button
                variant="primary"
                size="sm"
                disabled={reviewBusy || !onReview}
                onClick={() => onReview?.(task.id)}
              >
                {reviewBusy ? 'Saving…' : 'Mark reviewed'}
              </Button>
              {embedded && onAskForChanges && (
                <Button variant="secondary" size="sm" onClick={onAskForChanges}>
                  Ask {agentName}
                </Button>
              )}
              <span className={styles.tiny}>
                {isJoeScoutLive
                  ? "Moves this to History. Picks you haven't decided on stay on your Community page."
                  : "Moves this to History. It doesn't approve or publish anything."}
              </span>
            </div>
          )}
          {!canAct && isAudit && task.state === 'done' && (
            <div className={styles.noteBar}>
              Saved to your records — nothing here needs your approval. If there&apos;s follow-up,{' '}
              {agentName} opens it as its own task.
            </div>
          )}
          {!canAct &&
            !canCancel &&
            isAudit &&
            (task.state === 'ready' || task.state === 'blocked') && (
              <div className={styles.noteBar}>
                Scheduled — results appear here once this cycle runs.
              </div>
            )}
          {canWithdraw && (
            <div className={styles.actions}>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => setDialog('withdraw')}
              >
                Withdraw…
              </Button>
              <span className={styles.tiny}>
                Agency only. Closes this without approving or rejecting it.
              </span>
            </div>
          )}
        </DecisionSlot>
      )}

      <ConfirmDialog
        isOpen={dialog !== null}
        onClose={closeDialog}
        onConfirm={() => {
          const reason = dialogReason.trim() || undefined;
          if (dialog === 'skip') onSkip?.(task.id, reason);
          else if (dialog === 'cancel') onCancel?.(task.id, reason);
          else if (dialog === 'withdraw') onWithdraw?.(task.id, withdrawKind, reason);
          else onReject?.(task.id, reason);
          closeDialog();
        }}
        title={
          dialog === 'skip'
            ? `Skip this ${skipNoun}?`
            : dialog === 'cancel'
              ? 'Cancel this task?'
              : dialog === 'withdraw'
                ? 'Withdraw this suggestion?'
                : 'Reject this task?'
        }
        message={
          <>
            {dialog === 'withdraw' && (
              <fieldset className={styles.withdrawChoices}>
                <legend className={styles.rejectPrompt}>Why is it being withdrawn?</legend>
                <label className={styles.withdrawChoice}>
                  <input
                    type="radio"
                    name="withdraw-kind"
                    checked={withdrawKind === 'out_of_date'}
                    onChange={() => setWithdrawKind('out_of_date')}
                  />
                  <span>
                    <strong>Out of date</strong>
                    <span className={styles.tiny}>
                      The data has moved on. {agentName} may suggest something similar again once
                      the account supports it.
                    </span>
                  </span>
                </label>
                <label className={styles.withdrawChoice}>
                  <input
                    type="radio"
                    name="withdraw-kind"
                    checked={withdrawKind === 'done_outside'}
                    onChange={() => setWithdrawKind('done_outside')}
                  />
                  <span>
                    <strong>Done outside PracticeNova</strong>
                    <span className={styles.tiny}>
                      The change was already made (for example in Google Ads). {agentName} counts it
                      as approved and won&apos;t propose it again.
                    </span>
                  </span>
                </label>
              </fieldset>
            )}
            <p className={styles.rejectPrompt}>
              {dialog === 'withdraw'
                ? 'The practice sees it as withdrawn in History, with your note. It is not stored as their guidance.'
                : dialog === 'skip'
                  ? 'No replacement will be written.'
                  : dialog === 'cancel'
                    ? slot
                      ? `${agentName} won't write this one, and no replacement follows.`
                      : isAudit
                        ? `${agentName} won't run this check now. The next one runs at its regular time.`
                        : `${agentName} won't start this work.`
                    : slot
                      ? `${agentName} will write a replacement using your reason. To go without a ${skipNoun} this time, use Skip this ${skipNoun} instead.`
                      : `${agentName} remembers why, so this won't be proposed again.`}
            </p>
            <textarea
              className={styles.noteInput}
              rows={3}
              autoFocus
              value={dialogReason}
              onChange={(e) => setDialogReason(e.target.value)}
              placeholder={
                dialog === 'withdraw'
                  ? 'Note for the practice (optional)'
                  : dialog === 'skip'
                    ? 'Why are you skipping this? (optional)'
                    : dialog === 'cancel'
                      ? 'Why are you cancelling this? (optional)'
                      : 'Why are you rejecting this? (optional)'
              }
            />
          </>
        }
        confirmLabel={
          dialog === 'skip'
            ? 'Skip post'
            : dialog === 'cancel'
              ? 'Cancel task'
              : dialog === 'withdraw'
                ? 'Withdraw'
                : 'Reject'
        }
        // The dialog's own dismiss button reads "Cancel", which here would be the action itself.
        cancelLabel={dialog === 'cancel' ? 'Keep task' : undefined}
        variant="destructive"
        isLoading={busy}
      />
    </div>
  );
}

// Where an approved Business Profile post or edit stands on Google. It runs in the background after
// approval, so approved work with no outcome yet is still on its way.
function PublicationStatus({
  publication,
  noun,
  busy,
  onRetry,
}: {
  publication?: ContentPublication;
  noun: 'post' | 'change' | 'setting' | 'page';
  busy: boolean;
  onRetry?: () => void;
}) {
  const isChange = noun === 'change';
  // A website change is a pull request: sent for review, live only once a person merges it.
  if (noun === 'page') {
    if (!publication)
      return <div className={styles.noteBar}>Approved. Sending the change to your website…</div>;
    if (publication.status === 'published')
      return (
        <div className={styles.noteBar}>
          {publication.stage === 'live'
            ? 'Live on your website.'
            : 'Sent for review. It goes live once it is merged.'}{' '}
          {publication.url && (
            <a href={publication.url} target="_blank" rel="noreferrer">
              View the change
            </a>
          )}
        </div>
      );
    return (
      <div className={styles.actions}>
        <span className={styles.tiny}>
          Not sent: {publication.failureReason ?? 'The change could not be sent to your website.'}
        </span>
        {onRetry && (
          <Button variant="secondary" size="sm" disabled={busy} onClick={onRetry}>
            {busy ? 'Retrying…' : 'Try again'}
          </Button>
        )}
      </div>
    );
  }
  // Betty's proposals apply to the texting program, not Google.
  if (noun === 'setting') {
    if (!publication)
      return (
        <div className={styles.noteBar}>Approved. Applying it to your text review settings…</div>
      );
    if (publication.status === 'published')
      return <div className={styles.noteBar}>{publication.summary ?? 'Applied.'}</div>;
    return (
      <div className={styles.actions}>
        <span className={styles.tiny}>
          Not applied: {publication.failureReason ?? 'The change could not be applied.'}
        </span>
        {onRetry && (
          <Button variant="secondary" size="sm" disabled={busy} onClick={onRetry}>
            {busy ? 'Retrying…' : 'Try again'}
          </Button>
        )}
      </div>
    );
  }
  if (!publication) {
    return (
      <div className={styles.noteBar}>
        {isChange
          ? 'Approved. Applying the change to your Google Business Profile…'
          : 'Approved. Publishing to your Google Business Profile…'}
      </div>
    );
  }
  if (publication.status === 'published') {
    const when = publication.publishedAt
      ? new Date(publication.publishedAt).toLocaleDateString()
      : null;
    return (
      <div className={styles.noteBar}>
        {isChange
          ? 'Applied to your Google Business Profile'
          : 'Published to your Google Business Profile'}
        {when ? ` on ${when}` : ''}.{' '}
        {publication.url && (
          <a href={publication.url} target="_blank" rel="noreferrer">
            View on Google
          </a>
        )}
      </div>
    );
  }
  return (
    <div className={styles.actions}>
      <span className={styles.tiny}>
        {isChange ? 'Not applied' : 'Not published'}:{' '}
        {publication.failureReason ??
          (isChange ? 'Google did not accept the change.' : 'Google did not accept the post.')}
        {publication.reconnectRequired ? ' Reconnect Google Business Profile, then try again.' : ''}
      </span>
      {onRetry && (
        <Button variant="secondary" size="sm" disabled={busy} onClick={onRetry}>
          {busy ? 'Retrying…' : 'Try again'}
        </Button>
      )}
    </div>
  );
}

function ThreadPane({
  taskId,
  locationId,
  onVersionTagClick,
  agentName,
}: {
  taskId: string;
  locationId: string;
  onVersionTagClick: (n: number) => void;
  agentName: string;
}) {
  const { messages, send, sending, retry, retrying } = useTaskThread(taskId, locationId, true);
  const [text, setText] = useState('');
  const streamRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const submit = () => {
    const t = text.trim();
    if (t && !sending) {
      send(t);
      setText('');
    }
  };
  // Enter sends (the universal chat gesture); Shift+Enter inserts a newline.
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className={styles.threadPane}>
      <div className={styles.threadLegend}>
        <span className={styles.legendVer}>✦ vN</span> {agentName} updated the draft — a new version
      </div>
      <div className={styles.stream} ref={streamRef}>
        {messages.length === 0 ? (
          <div className={styles.threadEmpty}>
            <strong>No conversation yet.</strong>
            <br />
            Message {agentName} about this task.
            <br />
            <span className={styles.tiny}>
              If {agentName} changes the draft, that&apos;s a new version; a plain answer leaves the
              version as it is.
            </span>
          </div>
        ) : (
          messages.map((m, index) =>
            m.kind === 'notice' ? (
              // From the system, not the agent: a reply that could not be finished. Retry only on
              // the latest message, so an older notice never re-runs a turn already answered.
              <Callout
                key={m.id}
                variant="warning"
                actions={
                  index === messages.length - 1 ? (
                    <Button variant="secondary" disabled={retrying} onClick={() => retry()}>
                      Retry
                    </Button>
                  ) : undefined
                }
              >
                {m.body}
              </Callout>
            ) : (
              <div key={m.id} className={m.direction === 'outbound' ? styles.msgOut : styles.msgIn}>
                <span className={styles.msgMeta} title={formatTimestamp(new Date(m.at))}>
                  {m.direction === 'outbound' ? agentName : 'You'}
                  {m.channel === 'email' ? ' · email' : ''}
                  {` · ${formatDate(new Date(m.at), 'MMM d, h:mm a')}`}
                </span>
                <ReplyQuote message={m} previous={messages[index - 1]} messages={messages} />
                <ReactMarkdown>{m.body}</ReactMarkdown>
                {m.direction === 'outbound' && (
                  <VersionTag message={m} onClick={onVersionTagClick} />
                )}
              </div>
            ),
          )
        )}
      </div>
      <div className={styles.compose}>
        <textarea
          className={styles.noteInput}
          rows={2}
          value={text}
          placeholder={`Message ${agentName}… (Enter to send, Shift+Enter for a new line)`}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <Button variant="primary" disabled={sending || !text.trim()} onClick={submit}>
          Send
        </Button>
      </div>
    </div>
  );
}

// An answer to an earlier message than the one just above it quotes the message it answers,
// so a reply that lands after a newer question still reads as the answer to the right one.
function ReplyQuote({
  message,
  previous,
  messages,
}: {
  message: AgentMessage;
  previous: AgentMessage | undefined;
  messages: AgentMessage[];
}) {
  const text = replyQuoteText(message, previous, messages);
  if (!text) return null;
  return <div className={styles.msgQuote}>Replying to: “{text}”</div>;
}

// A Sam message that produced a new draft version gets a ✦ vN tag (clickable → jumps the output
// pane to it). A pure answer (no version) gets no tag — nothing to point at.
function VersionTag({ message, onClick }: { message: AgentMessage; onClick: (n: number) => void }) {
  if (message.producedVersion == null) return null;
  return (
    <button
      type="button"
      className={styles.msgTag}
      onClick={() => onClick(message.producedVersion as number)}
    >
      ✦ v{message.producedVersion}
    </button>
  );
}
